// src/components/Dashboard/SiteInformationCard.jsx
import { Typography } from '@mui/material';
import { motion } from 'framer-motion';
import { Clock, Info, MapPin, RefreshCw } from 'lucide-react'; // Added RefreshCw icon
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import employeeIcon from '../../assets/employee.png';
import Button from '../../components/ui/button.js'; // Ensure .js extension
import PrimaryButton from '../../components/ui/PrimaryButton.js'; // Import PrimaryButton
import ProfileImage from '../../components/ui/ProfileImage.js'; // Import ProfileImage component
import { dashboardApi } from './dashboardApi.js'; // Import API functions

// Utility to combine Tailwind classes (defined locally for self-containence)
const combineClasses = (...classes) => classes.filter(Boolean).join(' ');

// Glassmorphism Styles (consistent with other components)
const glassmorphism = 'bg-white/70 backdrop-blur-md rounded-xl border border-gray-200';
const darkText = 'text-gray-900'; // For general text on light backgrounds
const lightText = 'text-white'; // For text on darker backgrounds

// Framer Motion variants
const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

// Mock data for when no specific site is passed
const defaultMockSiteInfo = { id: 'default-site', name: 'No Site Selected', address: 'Select a location on the map or search.', supervisors: 0, employees: 0 };


// Modified SiteInformationCard to accept 'site' and 'siteEmployees' props
// Added onShowAllEmployees prop for the "Employees at Site" info button
// Added onClose prop for closing the sidebar
// Added onShiftSelected prop for shift selection callback
// Added onEmployeesForMap prop to pass employees with location data for map display
// Added onEmployeeClick prop to handle employee card clicks and display on map
const SiteInformationCard = ({ site, siteEmployees, onFocusMap, onEditSite, setIsSiteScheduleInPanel, onShowAllEmployees, onClose, onShiftSelected, onEmployeesForMap, onEmployeeClick }) => {
    const currentSite = site || defaultMockSiteInfo;
    const navigate = useNavigate();
    
    // State for shifts
    const [shifts, setShifts] = useState([]);
    const [loadingShifts, setLoadingShifts] = useState(false);
    const [selectedShiftId, setSelectedShiftId] = useState(null);

    // State for shift-specific employees
    const [shiftEmployees, setShiftEmployees] = useState([]);
    const [loadingEmployees, setLoadingEmployees] = useState(false);

    // Get current date in YYYY-MM-DD format
    const getCurrentDate = () => {
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    // Check if a shift is currently active
    const isShiftCurrentlyActive = (shift) => {
        const startTime = shift.start_time?.toString() || '';
        const endTime = shift.end_time?.toString() || '';

        if (!startTime || !endTime) {
            return false;
        }

        try {
            const now = new Date();
            const currentMinutes = now.getHours() * 60 + now.getMinutes();
            
            // Parse shift times - handle both 12-hour and 24-hour formats
            const parseTime = (timeStr) => {
                let hours, minutes;
                
                // Check if it's in 12-hour format (contains AM/PM)
                if (timeStr.includes('AM') || timeStr.includes('PM') || timeStr.includes('am') || timeStr.includes('pm')) {
                    const time = timeStr.toUpperCase().replace(/\s/g, '');
                    const match = time.match(/(\d+):(\d+)\s?(AM|PM)/);
                    if (match) {
                        hours = parseInt(match[1], 10);
                        minutes = parseInt(match[2], 10);
                        const period = match[3];
                        
                        if (period === 'PM' && hours !== 12) {
                            hours += 12;
                        } else if (period === 'AM' && hours === 12) {
                            hours = 0;
                        }
                    } else {
                        return null;
                    }
                } else {
                    // 24-hour format
                    const parts = timeStr.split(':');
                    if (parts.length < 2) return null;
                    hours = parseInt(parts[0], 10);
                    minutes = parseInt(parts[1], 10);
                }
                
                return hours * 60 + minutes;
            };
            
            const startMinutes = parseTime(startTime);
            const endMinutes = parseTime(endTime);
            
            if (startMinutes === null || endMinutes === null) {
                return false;
            }

            // Handle shifts that span across midnight
            if (endMinutes < startMinutes) {
                // Shift spans midnight (e.g., 07:00 PM to 07:00 AM)
                return currentMinutes >= startMinutes || currentMinutes <= endMinutes;
            } else {
                // Normal shift within same day
                return currentMinutes >= startMinutes && currentMinutes <= endMinutes;
            }
        } catch (error) {
            console.error('Error parsing shift time:', error);
            return false;
        }
    };

    // Format shift timing for display (handles both 12-hour and 24-hour formats)
    const formatShiftTiming = (shift) => {
        const startTime = shift.start_time || '';
        const endTime = shift.end_time || '';
        
        if (!startTime || !endTime) {
            return '';
        }

        try {
            // For 12-hour format (e.g., "09:00 AM"), just return as is
            // For 24-hour format, convert to 12-hour format
            const formatTime = (timeStr) => {
                // Check if already in 12-hour format (contains AM/PM)
                if (timeStr.includes('AM') || timeStr.includes('PM') || timeStr.includes('am') || timeStr.includes('pm')) {
                    return timeStr;
                }
                
                // Assume 24-hour format and convert to 12-hour
                const parts = timeStr.split(':');
                if (parts.length >= 2) {
                    let hours = parseInt(parts[0], 10);
                    const minutes = parts[1];
                    const period = hours >= 12 ? 'PM' : 'AM';
                    
                    if (hours === 0) {
                        hours = 12;
                    } else if (hours > 12) {
                        hours = hours - 12;
                    }
                    
                    return `${hours}:${minutes} ${period}`;
                }
                return timeStr;
        };
        
        return `${formatTime(startTime)} - ${formatTime(endTime)}`;
        } catch (error) {
            console.error('Error formatting shift timing:', error);
            return '';
        }
    };

    // Fetch shifts when site changes
    useEffect(() => {
    const fetchSiteShifts = async () => {
        if (!currentSite || currentSite.id === 'default-site') {
            setShifts([]);
            setSelectedShiftId(null);
            return;
        }

        setLoadingShifts(true);
            try {
                const formattedDate = getCurrentDate();
            const data = await dashboardApi.getSiteShifts(currentSite.id, formattedDate);
            
            // Extract shifts from the response
            let shiftsData = [];
                if (data?.shifts && Array.isArray(data.shifts)) {
                shiftsData = data.shifts;
            } else {
                console.log('No shifts found in response or invalid format');
                shiftsData = [];
            }

            // Validate and convert shift data
            const validShifts = [];
            const usedIds = new Set(); // Track used IDs to ensure uniqueness

                shiftsData.forEach((shift, index) => {
                    try {
                        if (typeof shift === 'object' && shift !== null) {
                            // Generate unique shift ID
                            let originalId = shift.ss_id?.toString() || '';
                            let uniqueId = originalId || `shift_${index}`;

                            // Ensure uniqueness by adding suffix if needed
                            let suffix = 1;
                            while (usedIds.has(uniqueId)) {
                                uniqueId = originalId ? `${originalId}_${suffix}` : `shift_${index}_${suffix}`;
                                suffix++;
                            }
                            usedIds.add(uniqueId);

                            // Calculate employee count from user_ids or user_datas
                            const calculateEmployeeCount = (shiftData) => {
                                if (shiftData.user_ids && Array.isArray(shiftData.user_ids)) {
                                    return shiftData.user_ids.length;
                                }
                                if (shiftData.user_datas && Array.isArray(shiftData.user_datas)) {
                                    return shiftData.user_datas.length;
                                }
                                return 0;
                            };

                            // Ensure all required fields are properly typed
                            const validShift = {
                                shift_id: uniqueId,
                                shift_name: `Shift ${shift.shift_index?.toString() || (index + 1).toString()}`,
                                employee_count: calculateEmployeeCount(shift),
                                is_active: true, // All shifts from getSiteShifts are considered active
                                start_time: shift.shift_start_time?.toString() || '',
                                end_time: shift.shift_end_time?.toString() || '',
                                shift_index: shift.shift_index?.toString() || '',
                                ss_id: shift.ss_id?.toString() || '',
                                user_ids: shift.user_ids || [],
                                user_datas: shift.user_datas || [],
                            };
                            validShifts.push(validShift);
                        }
                    } catch (error) {
                        console.error(`Error processing shift at index ${index}:`, error, shift);
                    }
                });

                setShifts(validShifts);
                
                // Auto-select the first currently active shift
                let activeShiftId = null;
                for (const shift of validShifts) {
                    if (isShiftCurrentlyActive(shift)) {
                        activeShiftId = shift.ss_id?.toString();
                        break;
                    }
                }
                
                const shiftIdToSelect = activeShiftId || (validShifts.length > 0 ? validShifts[0].ss_id?.toString() : null);
                setSelectedShiftId(shiftIdToSelect);
                
                // Notify parent component about the selected shift
                if (shiftIdToSelect && onShiftSelected) {
                    onShiftSelected(shiftIdToSelect);
                }

                console.log(`Successfully processed ${validShifts.length} shifts for site ${currentSite.id}`);
            } catch (error) {
                console.error('Error fetching site shifts:', error);
                setShifts([]);
                setSelectedShiftId(null);
            } finally {
                setLoadingShifts(false);
            }
        };

        fetchSiteShifts();
    }, [currentSite?.id, onShiftSelected]);

    // Fetch shift-specific employees when shift is selected
    useEffect(() => {
        const fetchShiftEmployees = async () => {
            if (!currentSite || currentSite.id === 'default-site' || !selectedShiftId) {
                setShiftEmployees([]);
                return;
            }

            setLoadingEmployees(true);
            try {
                console.log('🔍 Fetching employees for shift:', selectedShiftId);
                
                // Fetch employees assigned to this shift
                const assignedUsers = await dashboardApi.getAssignedUsersBySiteAndShift(
                    currentSite.id,
                    selectedShiftId
                );
                
                console.log('🔍 Assigned users response:', assignedUsers);
                
                // Fetch duty data for all users
                const dutyData = await dashboardApi.getAllUsersWithDutyData();
                const usersData = dutyData?.users || [];
                console.log('🔍 Duty data users count:', usersData.length);
                
                // Process assigned users and filter by on-duty status
                const processedEmployees = [];
                
                // Handle the API response - it should be an array of user objects
                let usersToProcess = [];
                if (Array.isArray(assignedUsers)) {
                    usersToProcess = assignedUsers;
                } else if (assignedUsers && typeof assignedUsers === 'object') {
                    // If it's wrapped in an object, try to extract the users array
                    usersToProcess = assignedUsers.users || assignedUsers.usersData || [];
                }
                
                if (usersToProcess.length > 0) {
                    // Fetch profile images for all employees in parallel
                    const employeesWithPhotos = await Promise.all(
                        usersToProcess.map(async (user) => {
                            // Find duty status for this user
                            const userData = usersData.find(u => u.user_id === user.user_id);
                            const isOnDuty = userData?.on_duty === true;
                            
                            let profileImageUrl = employeeIcon; // Default to employee icon
                            try {
                                if (user.user_id) {
                                    profileImageUrl = await dashboardApi.getProfilePhoto(user.user_id);
                                }
                            } catch (error) {
                                console.error(`Failed to get profile photo for ${user.user_name}:`, error);
                                // Keep default employee icon
                            }
                            
                            return {
                                id: user.user_id,
                                name: user.user_name,
                                role: user.spec_role || user.role_name || 'Employee', // Use spec_role instead of role_name
                                image: profileImageUrl || null,
                                officeId: user.site_id || 'N/A',
                                status: userData?.on_duty ? 'On Duty' : 'Off Duty',
                                note: 'No Note', // Placeholder until notes are fetched
                                lat: user.location?.latitude || user.last_location?.latitude,
                                lng: user.location?.longitude || user.last_location?.longitude,
                                isOnDuty: isOnDuty,
                            };
                        })
                    );
                    
                    processedEmployees.push(...employeesWithPhotos);
                }
                
                console.log('🔍 Processed shift employees:', processedEmployees);
                setShiftEmployees(processedEmployees);
            } catch (error) {
                console.error('Error fetching shift employees:', error);
                setShiftEmployees([]);
            } finally {
                setLoadingEmployees(false);
            }
        };

        fetchShiftEmployees();
    }, [selectedShiftId, currentSite?.id]);

    // Handle shift selection
    const handleShiftSelect = (shiftId) => {
        setSelectedShiftId(shiftId);
        if (onShiftSelected) {
            onShiftSelected(shiftId);
        }
    };
    
    // Display employees based on shift selection
    const displayedEmployees = selectedShiftId ? shiftEmployees : siteEmployees;

    // Notify parent when employees with location data are ready for map display
    useEffect(() => {
        if (displayedEmployees && displayedEmployees.length > 0 && onEmployeesForMap) {
            // Filter employees with valid location data
            const employeesForMap = displayedEmployees.filter(
                emp => emp.lat != null && emp.lng != null
            );
            if (employeesForMap.length > 0) {
                onEmployeesForMap(employeesForMap);
            }
        }
    }, [displayedEmployees, onEmployeesForMap]);

    // Refresh shift data
    const handleRefreshShifts = async () => {
        if (!currentSite || currentSite.id === 'default-site') return;
        
        setLoadingShifts(true);
        try {
            const formattedDate = getCurrentDate();
            const data = await dashboardApi.getSiteShifts(currentSite.id, formattedDate);
            
            let shiftsData = [];
            if (data?.shifts && Array.isArray(data.shifts)) {
                shiftsData = data.shifts;
            }

            const validShifts = [];
            const usedIds = new Set();

            shiftsData.forEach((shift, index) => {
                try {
                    if (typeof shift === 'object' && shift !== null) {
                        let originalId = shift.ss_id?.toString() || '';
                        let uniqueId = originalId || `shift_${index}`;

                        let suffix = 1;
                        while (usedIds.has(uniqueId)) {
                            uniqueId = originalId ? `${originalId}_${suffix}` : `shift_${index}_${suffix}`;
                            suffix++;
                        }
                        usedIds.add(uniqueId);

                        const calculateEmployeeCount = (shiftData) => {
                            if (shiftData.user_ids && Array.isArray(shiftData.user_ids)) {
                                return shiftData.user_ids.length;
                            }
                            if (shiftData.user_datas && Array.isArray(shiftData.user_datas)) {
                                return shiftData.user_datas.length;
                            }
                            return 0;
                        };

                        const validShift = {
                            shift_id: uniqueId,
                            shift_name: `Shift ${shift.shift_index?.toString() || (index + 1).toString()}`,
                            employee_count: calculateEmployeeCount(shift),
                            is_active: true,
                            start_time: shift.shift_start_time?.toString() || '',
                            end_time: shift.shift_end_time?.toString() || '',
                            shift_index: shift.shift_index?.toString() || '',
                            ss_id: shift.ss_id?.toString() || '',
                            user_ids: shift.user_ids || [],
                            user_datas: shift.user_datas || [],
                        };
                        validShifts.push(validShift);
                    }
                } catch (error) {
                    console.error(`Error processing shift at index ${index}:`, error);
                }
            });

            setShifts(validShifts);
            
            let activeShiftId = null;
            for (const shift of validShifts) {
                if (isShiftCurrentlyActive(shift)) {
                    activeShiftId = shift.ss_id?.toString();
                    break;
                }
            }
            
            const shiftIdToSelect = activeShiftId || (validShifts.length > 0 ? validShifts[0].ss_id?.toString() : null);
            setSelectedShiftId(shiftIdToSelect);
            
            if (shiftIdToSelect && onShiftSelected) {
                onShiftSelected(shiftIdToSelect);
            }
        } catch (error) {
            console.error('Error refreshing shifts:', error);
        } finally {
            setLoadingShifts(false);
        }
    };

    const handleOpenSchedule = () => {
        if (currentSite.id !== 'default-site') {
            // Get current date
            const currentDate = getCurrentDate();
            
            // Get the selected shift data
            const selectedShift = shifts.find(shift => shift.ss_id?.toString() === selectedShiftId);
            
            // Build URL parameters
            const params = new URLSearchParams({
                siteId: currentSite.id,
                siteName: currentSite.name,
                date: currentDate,
            });
            
            // Add shift information if available
            if (selectedShift) {
                params.append('shiftIndex', selectedShift.shift_index || '');
                params.append('shiftStartTime', selectedShift.start_time || '');
                params.append('shiftEndTime', selectedShift.end_time || '');
                params.append('shiftId', selectedShift.ss_id || '');
            }
            
            // Navigate to Schedule A Shift screen with parameters
            navigate(`/shift-management-schedule-a-shift?${params.toString()}`);
        }
    };

    // Handler for focusing map on the current site
    const handleFocusMap = () => {
        if (currentSite.id !== 'default-site' && onFocusMap && currentSite.lat && currentSite.lng) {
            onFocusMap(currentSite);
        } else {
            console.warn("Cannot focus map: No site selected or missing coordinates/onFocusMap prop.");
        }
    };

    // Calculate live shifts based on fetched shifts
    const activeShifts = shifts.filter(shift => isShiftCurrentlyActive(shift));
    const liveShiftsCount = activeShifts.length;
    const liveShiftDisplay = liveShiftsCount > 0 ? `${liveShiftsCount} Live Shift(s)` : 'No Live Shifts';


    return (
        <motion.div
            className={combineClasses(
                'p-4 space-y-4 h-full flex flex-col', // Added flex-col and h-full for layout
                darkText // Ensure text is dark on this light background
            )}
            variants={cardVariants}
            initial="hidden"
            animate="visible"
        >
            {/* Header with Site Info title and close button */}
            <div className="self-stretch inline-flex justify-between items-center">
                <div className="justify-start text-black text-xl font-bold font-['Figtree'] leading-normal">Site Info</div>
                <div 
                    className="w-8 h-8 bg-neutral-50/50 rounded-full shadow-[0px_15px_30px_0px_rgba(0,0,0,0.10)] shadow-[0px_5px_20.100000381469727px_0px_rgba(255,255,255,0.10)] shadow-[inset_0px_4px_25px_0px_rgba(255,255,255,0.35)] border border-white/0 backdrop-blur-[30px] flex items-center justify-center cursor-pointer hover:bg-neutral-100/50 transition-colors"
                    onClick={onClose}
                    title="Close Site Info"
                >
                    <svg className="w-4 h-4 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </div>
            </div>

            {/* Site Info Section (mimicking 'Ride' box) */}
            <div className={combineClasses(glassmorphism, 'p-4 space-y-2 flex-shrink-0')}> {/* Added flex-shrink-0 */}
                <div className="flex justify-between items-center mb-2">
                    <Typography variant="body1" className="font-bold">Locate Site on Map</Typography>
                    {currentSite.id !== 'default-site' && (
                        <Button
                            onClick={handleFocusMap} // Changed to call handleFocusMap
                            className="bg-blue-500/20 text-blue-600 hover:bg-blue-500/30 p-2 rounded-full"
                            title="Focus on Map" // Added title for tooltip
                        >
                            <MapPin className="w-4 h-4" /> {/* Changed icon to MapPin */}
                        </Button>
                    )}
                </div>
                <div className="space-y-1">
                    <Typography variant="body2"><strong>Name:</strong> {currentSite.name}</Typography>
                    <Typography variant="body2"><strong>Address:</strong> {currentSite.address}</Typography>
                    {/* <Typography variant="body2"><strong>Higher employees:</strong> {currentSite.supervisors}</Typography>
                    <Typography variant="body2"><strong>Employees:</strong> {currentSite.employees}</Typography> */}
                </div>
            </div>

            {/* Live Shift(s) Section - Display shift buttons */}
            {/* <div className={combineClasses(glassmorphism, 'p-4 space-y-2 flex-shrink-0')}> */}
                <div className="flex justify-between items-center mb-2">
                    <Typography variant="h6" className="font-bold flex items-center gap-2">
                        <Clock className="w-5 h-5" /> Live Shift(s)
                    </Typography>
                    {!loadingShifts && shifts.length > 0 && (
                        <Button
                            onClick={handleRefreshShifts}
                            className="p-1 rounded-full hover:bg-gray-100 transition-colors"
                            title="Refresh Shifts"
                        >
                            <RefreshCw className="w-4 h-4 text-gray-600" />
                        </Button>
                    )}
                </div>
                
                {loadingShifts ? (
                    <div className="flex justify-center items-center py-4">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-600"></div>
                    </div>
                ) : shifts.length === 0 ? (
                    <div className="flex flex-col items-center py-4">
                        <Typography variant="body2" className="text-gray-600 text-center mb-2">
                            No shifts assigned
                        </Typography>
                        <Button
                            onClick={handleRefreshShifts}
                            className="p-2 text-sm text-blue-600 hover:text-blue-700"
                        >
                            <RefreshCw className="w-4 h-4 mr-1" />
                            Refresh
                        </Button>
                    </div>
                ) : (
                    <div className="w-full">
                        <select
                            value={selectedShiftId || ''}
                            onChange={(e) => handleShiftSelect(e.target.value)}
                            className="w-full px-4 py-3 rounded-lg bg-white/70 text-gray-900 border border-gray-300 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-sm font-medium"
                        >
                            <option value="" disabled>Select a shift</option>
                            {shifts.map((shift) => {
                                const shiftId = shift.ss_id?.toString() || '';
                                const shiftName = shift.shift_name || 'Shift';
                                const shiftIndex = shift.shift_index?.toString() || '';
                                const employeeCount = shift.employee_count || 0;
                                const isCurrentlyActive = isShiftCurrentlyActive(shift);
                                const timing = formatShiftTiming(shift);

                                // Only show currently active shifts
                                if (!isCurrentlyActive) {
                                    return null;
                                }
                                
                                // Create display name with timing in format "shift-1 (09:00 AM - 07:00 PM)"
                                let displayName;
                                if (timing && shiftIndex) {
                                    displayName = `Shift-${shiftIndex} (${timing}) - ${employeeCount} employee${employeeCount !== 1 ? 's' : ''}`;
                                } else if (timing) {
                                    displayName = `${timing} - ${employeeCount} employee${employeeCount !== 1 ? 's' : ''}`;
                                } else if (shiftIndex) {
                                    displayName = `Shift-${shiftIndex} - ${employeeCount} employee${employeeCount !== 1 ? 's' : ''}`;
                                } else {
                                    displayName = `${shiftName} - ${employeeCount} employee${employeeCount !== 1 ? 's' : ''}`;
                                }

                                return (
                                    <option key={shiftId || shift.shift_id} value={shiftId}>
                                        {displayName}
                                    </option>
                                );
                            })}
                        </select>
                    </div>
                )}
            {/* </div> */}

            {/* Employee Section (mimicking 'Employee' box) */}
            <div className={combineClasses(glassmorphism, 'p-4 space-y-2 flex-1 flex flex-col')}> {/* flex-1 and flex-col */}
                <div className="flex justify-between items-center mb-2 flex-shrink-0"> {/* Added flex-shrink-0 */}
                    <Typography variant="h6" className="font-bold">Employees at Site</Typography>
                    {currentSite.id !== 'default-site' && (
                        <Button
                            onClick={(e) => {
                                e.stopPropagation(); // <--- ADDED: Stop event propagation
                                if (onShowAllEmployees) {
                                    onShowAllEmployees(displayedEmployees); // Trigger showing the employee info table
                                }
                            }}
                            className="bg-blue-500/20 text-blue-600 hover:bg-blue-500/30 p-2 rounded-full"
                            aria-label="View All Employees" // Updated aria-label
                            title="View All Employees" // Updated title
                        >
                            <Info className="w-4 h-4" />
                        </Button>
                    )}
                </div>
                {loadingEmployees ? (
                    <div className="flex justify-center items-center py-4">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-600"></div>
                    </div>
                ) : displayedEmployees.length > 0 ? (
                    <ul className="space-y-2 overflow-y-auto custom-scrollbar max-h-80"> {/* Fixed max height to show ~4-5 employees before scrolling */}
                        {displayedEmployees.map(employee => (
                            <li 
                                key={employee.id} 
                                onClick={() => onEmployeeClick && onEmployeeClick(employee)} 
                                className="flex items-center justify-between p-2 bg-white/50 rounded-md shadow-sm border border-gray-100 cursor-pointer hover:bg-white/70 transition-colors"
                            >
                                <div className="flex items-center">
                                    <ProfileImage 
                                        userId={employee.id}
                                        userName={employee.name}
                                        imagePath={employee.image}
                                        size="sm"
                                        className="rounded-full mr-3"
                                        showLoading={false}
                                    />
                                    <div>
                                        <Typography variant="body1" className="font-medium">{employee.name}</Typography>
                                        <Typography variant="body2" className="text-gray-600">{employee.role}</Typography>
                                    </div>
                                </div>
                                <span className={combineClasses(
                                    "px-2 py-1 rounded-full text-xs font-semibold",
                                    employee.status === 'On Duty' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                )}>
                                    {employee.status}
                                </span>
                            </li>
                        ))}
                    </ul>
                ) : (
                    <Typography variant="body2" className="text-center text-gray-600 p-4 flex-1 flex items-center justify-center">
                        {currentSite.id === 'default-site' 
                            ? 'Select a site to view employees.' 
                            : selectedShiftId 
                            ? 'No employees assigned to this shift.' 
                            : 'No employees assigned to this site.'}
                    </Typography>
                )}
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col gap-3 mt-4 flex-shrink-0"> {/* Added flex-shrink-0 */}
                {/* Modify Site Schedule button (this one should still open the schedule) */}
                <PrimaryButton
                    className="w-full"
                    onClick={handleOpenSchedule} // This still calls the internal handler for schedule
                    disabled={currentSite.id === 'default-site'} // Disable if no site selected
                >
                    {/* <ListChecks className="mr-2 w-4 h-4" /> */}
                    Modify Site Schedule
                </PrimaryButton>
            </div>
        </motion.div>
    );
};

export default SiteInformationCard;
